import { create } from "zustand";
import { CreateJob, FileMetadata, Job, SoftwareSettingsMetadata } from "@domain/schemas/BI";
import type { MergeNodePoolOption } from "@domain/valueObjects";
import { resolvePipelineType } from "@domain/software";
import { JobPriorityEnumSchema, JobPipelineStepDetail, SoftwareEnumSchema, JobPipelineTypeSchema, PipelineStepEnumSchema, QuantificationMethodEnumSchema } from "@domain/schemas/BI";

/**
 * The in-progress job form.
 *
 * `projectId` is required on `CreateJob`, but a draft legitimately has no project
 * yet - the user may not have chosen one, or may have no active context. It is
 * therefore omitted from the base type and re-added as nullable, so the compiler
 * forces the submit path to prove a project was resolved instead of letting an
 * empty string reach the API. `teamId` is form-local only and is never sent: the
 * backend derives the owning team from the project.
 */
export type CreateJobDraft = Omit<CreateJob, "projectId"> & {
  teamId?: string | null;
  projectId?: string | null;
};

/**
 * Narrows a draft to a payload the API will accept, or returns null when no
 * project has been resolved. `teamId` is dropped here rather than at the call
 * site so there is exactly one place that decides what crosses the wire.
 */
export const toCreateJobPayload = (draft: CreateJobDraft): CreateJob | null => {
  const projectId = draft.projectId?.trim();
  if (!projectId) {
    return null;
  }

  const { teamId: _teamId, ...rest } = draft;
  return { ...rest, projectId };
};

const STEP_TYPE_TO_STABLE_ID: Partial<Record<PipelineStepEnumSchema, string>> = {
  [PipelineStepEnumSchema.PulsarStep1]: "22222222-2222-2222-2222-222222222222",
  [PipelineStepEnumSchema.LibraryGeneration]: "55555555-5555-5555-5555-555555555555",
  [PipelineStepEnumSchema.DiaAnalysis]: "66666666-6666-6666-6666-666666666666",
  [PipelineStepEnumSchema.SneMerge]: "77777777-7777-7777-7777-777777777777",
  [PipelineStepEnumSchema.PsarMerging]: "99999999-9999-9999-9999-999999999999",
};

const toFileMetadata = (paths?: string[] | null): FileMetadata[] => (paths ?? []).filter(Boolean).map((path) => ({ path, sizeInBytes: 0 }));

// Carries `sourcePath` through so a later job submission can disambiguate which configured root this
// selection came from, instead of the normalizer guessing (see StorageContextPathNormalizer).
const toSettingsFileMetadata = (settings: SoftwareSettingsMetadata): FileMetadata => ({
  path: settings.location ? `${settings.location}/${settings.name}` : settings.name,
  sizeInBytes: 0,
  sourcePath: settings.sourcePath ?? null,
});

// ✅ Default state
const defaultCreateJobState: CreateJobDraft = {
  quoteNumber: "",
  name: "",
  analysisName: "",
  software: SoftwareEnumSchema.Spectronaut,
  pipelineType: JobPipelineTypeSchema.DirectDIA, // Default
  priority: JobPriorityEnumSchema.Normal,
  batchSize: 1, // set it automatically to 1 as a default value.

  // fasta
  fastaFiles: [],
  rawFiles: [],
  libraryFiles: [],
  archiveFiles: [],
  softwareSettingsFile: undefined,
  searchSettingsFile: undefined,

  conditionSetupFile: undefined,
  modificationRepoFile: undefined,
  reportSettingsFiles: [],
  goFiles: [],
  mergeNodePoolOverride: null,
  // Label free by default. Only SpectroMine jobs may carry a value; the backend
  // rejects it for any other engine.
  quantificationMethod: undefined,
  pattern: "",
  outputPath: "",
  teamId: null,
  projectId: null,
};

// ✅ Store Type
type JobSetupStore = {
  CreateJobState: CreateJobDraft;
  sourceSoftwareVersion: string | null;
  //used to allow the user to choose if he wants to be redirected to the job list after creating the job or not(useful when want to create several jobs)
  redirectToJobList: boolean;
  showValidation: boolean;
  allowCreationButton: boolean;
  rerunAvailableSteps: JobPipelineStepDetail[];

  // Selected settings
  settingsDirectDIA: SoftwareSettingsMetadata | null;
  settingsSpectroMineSearch: SoftwareSettingsMetadata | null;
  modificationRepository: SoftwareSettingsMetadata | null;
  conditionSetup: SoftwareSettingsMetadata | null;
  reportSettings: SoftwareSettingsMetadata[] | null;
  goSettings: SoftwareSettingsMetadata[] | null;
  mergeNodePoolOptions: MergeNodePoolOption[];
  mergeNodePoolOptionsLoading: boolean;
  mergeNodePoolOptionsError: string | null;

  handleRedirectToJobListChange: () => void;
  resetCreateJobState: () => void;
  loadExistingJobForRerun: (job: Job) => void;
  setRerunAvailableSteps: (steps: JobPipelineStepDetail[], shouldOnlyIncludeFirstStep?: boolean) => void;
  setResumeFromStepId: (resumeFromStepId: string | null) => void;

  setQuoteNumber: (quoteNumber: string) => void;
  setName: (name: string) => void;
  setAnalysisName: (analysisName: string) => void;
  setPriority: (priority: JobPriorityEnumSchema) => void;
  setSoftware: (software: SoftwareEnumSchema) => void;
  setSoftwareVersion: (softwareVersion: string) => void;
  setBatchSize: (batchSize: number) => void;
  setOutputPath: (outputPath: string) => void;
  setJobContext: (teamId: string | null, projectId: string | null) => void;
  setJobFastaFiles: (fastaFiles: FileMetadata[]) => void;
  setJobRawFiles: (rawFiles: FileMetadata[]) => void;
  setJobLibraryFiles: (libraryFiles: FileMetadata[]) => void;
  setJobArchiveFiles: (archiveFiles: FileMetadata[]) => void;
  setPipelineType: (pipelineType: JobPipelineTypeSchema) => void;
  setMergeNodePoolOverride: (mergeNodePoolOverride: string | null) => void;
  setQuantificationMethod: (quantificationMethod: QuantificationMethodEnumSchema | undefined) => void;
  setMergeNodePoolOptionsLoading: (isLoading: boolean) => void;
  setMergeNodePoolOptions: (options: MergeNodePoolOption[]) => void;
  setMergeNodePoolOptionsError: (error: string | null) => void;

  // Settings handlers
  setDirectDIASettings: (settings: SoftwareSettingsMetadata[]) => void;
  setSpectroMineSearchSettings: (settings: SoftwareSettingsMetadata[]) => void;
  setModificationRepository: (settings: SoftwareSettingsMetadata[]) => void;
  setConditionSetup: (settings: SoftwareSettingsMetadata[]) => void;
  setReportSettings: (settings: SoftwareSettingsMetadata[]) => void;
  setGOSettings: (settings: SoftwareSettingsMetadata[]) => void;

  setAllowCreationButton: (shouldAllowCreation: boolean) => void;
  setShowValidation: (shouldShowValidation: boolean) => void;

  // Right panel tab control (0 = Raw Files, 1 = Libraries)
  rightPanelTab: number;
  setRightPanelTab: (tab: number) => void;
};

// ✅ Implementation
export const useJobSetupStore = create<JobSetupStore>()((set, get) => ({
  CreateJobState: defaultCreateJobState,
  sourceSoftwareVersion: null,
  redirectToJobList: true,
  showValidation: false,
  allowCreationButton: false,
  rerunAvailableSteps: [],

  // Settings
  settingsDirectDIA: null,
  settingsSpectroMineSearch: null,
  modificationRepository: null,
  conditionSetup: null,
  reportSettings: [],
  goSettings: [],
  mergeNodePoolOptions: [],
  mergeNodePoolOptionsLoading: false,
  mergeNodePoolOptionsError: null,

  handleRedirectToJobListChange: () => {
    set({ redirectToJobList: !get().redirectToJobList });
  },

  resetCreateJobState: () => {
    set({
      CreateJobState: {
        ...defaultCreateJobState,
        archiveFiles: [],
      },
      settingsDirectDIA: null,
      settingsSpectroMineSearch: null,
      modificationRepository: null,
      conditionSetup: null,
      reportSettings: [],
      goSettings: [],
      sourceSoftwareVersion: null,
      showValidation: false,
      allowCreationButton: false,
      rerunAvailableSteps: [],
      mergeNodePoolOptions: [],
      mergeNodePoolOptionsLoading: false,
      mergeNodePoolOptionsError: null,
      rightPanelTab: 0,
    });
  },

  loadExistingJobForRerun: (job) => {
    const toSingleSetting = (path?: string | null): SoftwareSettingsMetadata | null => {
      if (!path) return null;

      const normalizedPath = path.replaceAll("\\", "/");
      const segments = normalizedPath.split("/").filter(Boolean);
      const name = segments.at(-1) ?? normalizedPath;
      const location = segments.slice(0, -1).join("/");

      return {
        id: normalizedPath,
        name,
        location,
        createdBy: job.createdBy ?? "",
        software: job.software,
        softwareVersion: job.softwareVersion ?? "",
        settingsType: undefined as never,
      };
    };

    const toManySettings = (paths?: string[] | null): SoftwareSettingsMetadata[] =>
      (paths ?? []).filter(Boolean).map((path) => ({
        id: path,
        name: path.split("/").findLast(Boolean) ?? path,
        location: path.split("/").filter(Boolean).slice(0, -1).join("/"),
        createdBy: job.createdBy ?? "",
        software: job.software,
        softwareVersion: job.softwareVersion ?? "",
        settingsType: undefined as never,
      }));

    set({
      CreateJobState: {
        ...defaultCreateJobState,
        quoteNumber: "",
        name: job.analysisName ? (job.name ?? "").replace(new RegExp(`_${job.analysisName}$`), "") : (job.name ?? ""),
        analysisName: job.analysisName ?? "",
        description: job.description ?? "",
        software: job.software,
        softwareSettingsFile: job.softwareSettingsFile ? { path: job.softwareSettingsFile, sizeInBytes: 0 } : undefined,
        conditionSetupFile: job.conditionSetupFile ? { path: job.conditionSetupFile, sizeInBytes: 0 } : undefined,
        modificationRepoFile: job.modificationRepositoryFile ? { path: job.modificationRepositoryFile, sizeInBytes: 0 } : undefined,
        reportSettingsFiles: toFileMetadata(job.reportSettingsFile),
        goFiles: toFileMetadata(job.goFile),
        searchSettingsFile: job.searchSettingsFile ? { path: job.searchSettingsFile, sizeInBytes: 0 } : undefined,
        rawFiles: toFileMetadata(job.rawFiles),
        fastaFiles: toFileMetadata(job.fastaFiles),
        pattern: "",
        outputPath: job.outputPath ?? "",
        softwareVersion: job.softwareVersion ?? "",
        batchSize: 1,
        priority: job.priority,
        pipelineType: job.pipelineType ?? JobPipelineTypeSchema.DirectDIA,
        libraryFiles: toFileMetadata(job.libraryFiles),
        archiveFiles: toFileMetadata(job.archiveFiles),
        mergeNodePoolOverride: job.mergeNodePoolOverride ?? null,
        quantificationMethod: job.quantificationMethod ?? undefined,
        sourceCreatedAt: job.createdAt ?? null,
        sourceJobName: job.name ?? null,
        sourceAnalysisName: job.analysisName ?? null,
      },
      settingsDirectDIA: toSingleSetting(job.softwareSettingsFile),
      settingsSpectroMineSearch: toSingleSetting(job.searchSettingsFile),
      modificationRepository: toSingleSetting(job.modificationRepositoryFile),
      conditionSetup: toSingleSetting(job.conditionSetupFile),
      reportSettings: toManySettings(job.reportSettingsFile),
      goSettings: toManySettings(job.goFile),
      sourceSoftwareVersion: job.softwareVersion ?? null,
      showValidation: false,
      allowCreationButton: true,
      rerunAvailableSteps: [],
      mergeNodePoolOptions: get().mergeNodePoolOptions,
      mergeNodePoolOptionsLoading: false,
      mergeNodePoolOptionsError: get().mergeNodePoolOptionsError,
      rightPanelTab: 0,
    });
  },

  setRerunAvailableSteps: (steps, shouldOnlyIncludeFirstStep = false) => {
    const allowedTypes = new Set([
      PipelineStepEnumSchema.PulsarStep1,
      PipelineStepEnumSchema.LibraryGeneration,
      PipelineStepEnumSchema.DiaAnalysis,
      PipelineStepEnumSchema.SneMerge,
      PipelineStepEnumSchema.PsarMerging,
    ]);
    const filteredSteps = steps.filter((step) => allowedTypes.has(step.type as PipelineStepEnumSchema)).toSorted((a, b) => a.position - b.position);
    const availableSteps = shouldOnlyIncludeFirstStep ? filteredSteps.slice(0, 1) : filteredSteps;
    const defaultResumeStep = availableSteps[0] ? (STEP_TYPE_TO_STABLE_ID[availableSteps[0].type as PipelineStepEnumSchema] ?? null) : null;

    set((state) => ({
      rerunAvailableSteps: availableSteps,
      CreateJobState: {
        ...state.CreateJobState,
        resumeFromStepId: defaultResumeStep,
      },
    }));
  },

  setResumeFromStepId: (resumeFromStepId) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, resumeFromStepId },
    })),

  setQuoteNumber: (quoteNumber) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, quoteNumber },
    })),

  setName: (name) => set((state) => ({ CreateJobState: { ...state.CreateJobState, name } })),

  setAnalysisName: (analysisName) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, analysisName },
    })),

  setPriority: (priority) => set((state) => ({ CreateJobState: { ...state.CreateJobState, priority } })),

  setSoftware: (software) =>
    set((state) => {
      const settingsDirectDIA = state.settingsDirectDIA?.software === software ? state.settingsDirectDIA : null;
      const settingsSpectroMineSearch = software === SoftwareEnumSchema.SpectroMine && state.settingsSpectroMineSearch?.software === software ? state.settingsSpectroMineSearch : null;
      const conditionSetup = state.conditionSetup?.software === software ? state.conditionSetup : null;
      const reportSettings = (state.reportSettings ?? []).filter((settings) => settings.software === software);

      return {
        settingsDirectDIA,
        settingsSpectroMineSearch,
        conditionSetup,
        reportSettings,
        CreateJobState: {
          ...state.CreateJobState,
          software,
          // Engines support different pipelines, so a selection made for another
          // engine must not survive the switch.
          pipelineType: resolvePipelineType(software, state.CreateJobState.pipelineType),
          // -q is SpectroMine only and the backend returns 400 if it arrives for any
          // other engine, so a stale selection must not survive an engine switch.
          quantificationMethod: software === SoftwareEnumSchema.SpectroMine ? state.CreateJobState.quantificationMethod : undefined,
          // Engine-scoped files are kept only when their metadata matches the new engine.
          softwareSettingsFile: settingsDirectDIA ? toSettingsFileMetadata(settingsDirectDIA) : undefined,
          searchSettingsFile: settingsSpectroMineSearch ? toSettingsFileMetadata(settingsSpectroMineSearch) : undefined,
          conditionSetupFile: conditionSetup ? toSettingsFileMetadata(conditionSetup) : undefined,
          reportSettingsFiles: reportSettings.map((settings) => toSettingsFileMetadata(settings)),
        },
      };
    }),

  setSoftwareVersion: (softwareVersion) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, softwareVersion },
    })),

  setBatchSize: (batchSize) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, batchSize },
    })),

  setOutputPath: (outputPath) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, outputPath },
    })),

  setJobContext: (teamId, projectId) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, teamId, projectId },
    })),

  setJobFastaFiles: (fastaFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, fastaFiles },
    })),

  setJobRawFiles: (rawFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, rawFiles: rawFiles.filter(Boolean) },
    })),

  setJobLibraryFiles: (libraryFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, libraryFiles: libraryFiles.filter(Boolean) },
    })),

  setJobArchiveFiles: (archiveFiles) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, archiveFiles: archiveFiles.filter(Boolean) },
    })),

  setPipelineType: (pipelineType) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, pipelineType },
    })),

  setMergeNodePoolOverride: (mergeNodePoolOverride) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, mergeNodePoolOverride },
    })),

  setQuantificationMethod: (quantificationMethod) =>
    set((state) => ({
      CreateJobState: { ...state.CreateJobState, quantificationMethod },
    })),

  // ✅ Settings
  setDirectDIASettings: (settings) =>
    set((state) => ({
      settingsDirectDIA: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        softwareSettingsFile: settings[0] ? toSettingsFileMetadata(settings[0]) : undefined,
      },
    })),

  setSpectroMineSearchSettings: (settings) =>
    set((state) => ({
      settingsSpectroMineSearch: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        searchSettingsFile: settings[0] ? toSettingsFileMetadata(settings[0]) : undefined,
      },
    })),

  setModificationRepository: (settings) =>
    set((state) => ({
      modificationRepository: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        modificationRepoFile: settings[0] ? toSettingsFileMetadata(settings[0]) : undefined,
      },
    })),

  setConditionSetup: (settings) =>
    set((state) => ({
      conditionSetup: settings[0] || null,
      CreateJobState: {
        ...state.CreateJobState,
        conditionSetupFile: settings[0] ? toSettingsFileMetadata(settings[0]) : undefined,
      },
    })),

  setReportSettings: (settings) =>
    set((state) => ({
      reportSettings: settings,
      CreateJobState: {
        ...state.CreateJobState,
        reportSettingsFiles: settings.map((s) => toSettingsFileMetadata(s)),
      },
    })),

  setGOSettings: (settings) =>
    set((state) => ({
      goSettings: settings,
      CreateJobState: {
        ...state.CreateJobState,
        goFiles: settings.map((s) => toSettingsFileMetadata(s)),
      },
    })),

  setMergeNodePoolOptionsLoading: (isLoading) => set({ mergeNodePoolOptionsLoading: isLoading }),
  setMergeNodePoolOptions: (options) => set({ mergeNodePoolOptions: options, mergeNodePoolOptionsError: null }),
  setMergeNodePoolOptionsError: (error) => set({ mergeNodePoolOptionsError: error }),

  setAllowCreationButton: (shouldAllowCreation) => set({ allowCreationButton: shouldAllowCreation }),
  setShowValidation: (shouldShowValidation) => set({ showValidation: shouldShowValidation }),

  rightPanelTab: 0,
  setRightPanelTab: (tab) => set({ rightPanelTab: tab }),
}));
